import { Component } from "@theme/component";

/**
 * @typedef {object} Refs
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLElement} [current]
 * @extends Component<Refs>
 */

export class SwiperGallery extends Component {
	connectedCallback() {
		super.connectedCallback();
		this.#initSwiper();
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#swiper?.destroy();
	}

	#swiper = null;

	async #initSwiper() {
		const el = this.querySelector(".swiper");
		if (!el) return;

		if (typeof Swiper === "undefined") {
			await new Promise((resolve) => {
				const check = () =>
					typeof Swiper !== "undefined"
						? resolve()
						: requestAnimationFrame(check);
				check();
			});
		}

		const loop = this.dataset.loop !== "false";
		const autoplay = this.dataset.autoplay === "true";
		const speed = Number.parseFloat(this.dataset.autoplaySpeed) || 2.5;

		this.#swiper = new Swiper(el, {
			loop,
			slidesPerView: 1.2,
			breakpoints: {
				750: {
					slidesPerView: 3,
					allowTouchMove: false,
				},
			},
			...(autoplay
				? { autoplay: { delay: speed * 1000, disableOnInteraction: true } }
				: {}),
			on: {
				slideChange: () => this.#onSlideChange(),
			},
		});
	}

	select(input, _event, options = {}) {
		const index = typeof input === "number" ? input : Number.parseInt(input, 10);
		if (this.#swiper) {
			const speed = options.animate === false ? 0 : undefined;
			this.#swiper.slideToLoop(index, speed);
		}
	}

	next() {
		this.#swiper?.slideNext();
	}

	previous() {
		this.#swiper?.slidePrev();
	}

	#onSlideChange() {
		if (!this.#swiper) return;
		const index = this.#swiper.realIndex;

		this.refs.thumbnails?.forEach((el, i) => {
			el.setAttribute("aria-selected", `${i === index}`);
		});

		this.refs.dots?.forEach((el, i) => {
			el.setAttribute("aria-selected", `${i === index}`);
		});

		if (this.refs.current) {
			this.refs.current.textContent = `${index + 1}`;
		}
	}

	get slideshow() {
		return this;
	}
}

customElements.define("swiper-gallery", SwiperGallery);
